import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { login, isAuthenticated } from '../services/authService';
import { Layout, Row, Col, Card, Form, Input, Button, Typography, Alert, Spin } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const LoginPage = () => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const authenticated = await isAuthenticated();
                if (authenticated) {
                    navigate('/dashboard');
                } else {
                    setIsCheckingAuth(false);
                }
            } catch (err) {
                console.error("Fehler bei der Authentifizierungsprüfung:", err);
                setIsCheckingAuth(false);
            }
        };

        checkAuth();
    }, [navigate]);

    const onFinish = async (values) => {
        const { email, password } = values;

        if (!email || !password) {
            setError('Bitte geben Sie E-Mail und Passwort ein');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await login({ email, password });

            if (response.success) {
                // Check for redirect query parameter
                const params = new URLSearchParams(location.search);
                const redirectPath = params.get('redirect');

                // Determine navigation target
                const from = redirectPath || location.state?.from || '/dashboard';
                navigate(from);
            } else {
                setError(response.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Anmeldung fehlgeschlagen. Netzwerkproblem oder Serverfehler.');
        } finally {
            setLoading(false);
        }
    };

    if (isCheckingAuth) {
        return (
            <Layout style={{ minHeight: 'calc(100vh - 184px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin size="large" tip="Prüfe Anmeldung..." />
            </Layout>
        );
    }

    return (
        <Layout style={{ minHeight: 'calc(100vh - 184px)', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f2f5' }}>
            <Helmet>
                {/* <title>Anmelden | Snus Finder</title> */}
                <title>Anmelden | Snus Verzeichnis Deutschland</title>
                {/* <meta name="description" content="Melden Sie sich bei Snus Finder an, um Ihre Shops zu verwalten." /> */}
                <meta name="description" content="Melden Sie sich bei Ihrem Konto für Snus Verzeichnis Deutschland an." />
                {/* Prevent indexing of the login page */}
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Row justify="center" align="middle" style={{ width: '100%' }}>
                <Col xs={24} sm={16} md={12} lg={8} xl={6}>
                    <Card style={{ boxShadow: 'var(--box-shadow)' }}>
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <img src="/images/snus-finder-logo.svg" alt="Snus Finder Logo" style={{ height: '80px', marginBottom: '10px' }} />
                            <Title level={2}>Anmelden</Title>
                            <Paragraph>
                                Melden Sie sich an, um Ihre Shops zu verwalten.
                            </Paragraph>
                        </div>

                        {error && (
                            <Alert
                                message={error}
                                type="error"
                                showIcon
                                closable
                                onClose={() => setError(null)}
                                style={{ marginBottom: '24px' }}
                            />
                        )}

                        <Form
                            form={form}
                            name="login"
                            onFinish={onFinish}
                            layout="vertical"
                            requiredMark={false}
                        >
                            <Form.Item
                                name="email"
                                label="E-Mail"
                                rules={[
                                    { required: true, message: 'Bitte geben Sie Ihre E-Mail-Adresse ein!' },
                                    { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein!' }
                                ]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="Ihre E-Mail-Adresse" disabled={loading} />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Passwort"
                                rules={[{ required: true, message: 'Bitte geben Sie Ihr Passwort ein!' }]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Ihr Passwort" disabled={loading} />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                >
                                    Anmelden
                                </Button>
                            </Form.Item>
                        </Form>

                        <Row justify="space-between">
                            <Col>
                                <Link to="/forgot-password">Passwort vergessen?</Link>
                            </Col>
                            <Col>
                                Noch kein Konto? <Link to="/user-register">Registrieren</Link>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </Layout>
    );
};

export default LoginPage;